import { useMutation } from "@tanstack/react-query";
import {
  KeyRound,
  MoreHorizontal,
  Pencil,
  Plus,
  RotateCcw,
} from "lucide-react";
import {
  CanAccess,
  ListBase,
  RecordContextProvider,
  useCanAccess,
  useDataProvider,
  useListContext,
  useNotify,
  useRecordContext,
  useRedirect,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { BooleanField } from "@/components/admin/boolean-field";
import { Confirm } from "@/components/admin/confirm";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { List } from "@/components/admin/list";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from "@/components/ui/item";
import { Skeleton } from "@/components/ui/skeleton";

import { MobileContent } from "../layout/MobileContent";
import { MobileListHeader } from "../layout/MobileListHeader";
import { TopToolbar } from "../layout/TopToolbar";
import type { CrmDataProvider } from "../providers/types";
import type { Sale } from "../types";
import { TemporaryPasswordDialog } from "./TemporaryPasswordDialog";

const SalesListActions = () => (
  <TopToolbar>
    <CanAccess resource="sales" action="create">
      <CreateButton label="resources.sales.action.new" />
    </CanAccess>
  </TopToolbar>
);

/**
 * Per-row menu for administrators: edit the user or hand out a new
 * temporary password. Click events are stopped so the row's own
 * click-to-edit does not fire.
 *
 * Managing users is an administrator job, so the menu is gated on the
 * "create" permission: `useCanAccess` would otherwise fall back to the row
 * record, and a user may edit their own record (through their profile page).
 */
const SaleRowActions = (_props: { label?: string | boolean }) => {
  const record = useRecordContext<Sale>();
  const translate = useTranslate();
  const redirect = useRedirect();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { canAccess: canManageUsers } = useCanAccess({
    resource: "sales",
    action: "create",
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(
    null,
  );

  const { mutate: resetPassword, isPending } = useMutation({
    mutationKey: ["salesResetPassword", record?.id],
    mutationFn: async () => {
      if (!record) {
        throw new Error(translate("resources.sales.edit.record_not_found"));
      }
      return dataProvider.salesResetPassword(record.id);
    },
    onSuccess: ({ temporaryPassword: password }) => {
      setConfirmOpen(false);
      setTemporaryPassword(password);
    },
    onError: (error: Error) => {
      setConfirmOpen(false);
      notify(error?.message || "resources.sales.reset_password.error", {
        type: "error",
      });
    },
  });

  if (!record || !canManageUsers) return null;

  const name = `${record.first_name} ${record.last_name}`;

  return (
    <div
      className="flex justify-end"
      onClick={(event) => event.stopPropagation()}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label={translate("resources.sales.action.row_actions")}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => redirect("edit", "sales", record.id)}
          >
            <Pencil />
            {translate("ra.action.edit")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setConfirmOpen(true)}>
            <KeyRound />
            {translate("resources.sales.action.reset_password")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Confirm
        isOpen={confirmOpen}
        loading={isPending}
        title="resources.sales.reset_password.confirm_title"
        content="resources.sales.reset_password.confirm_content"
        titleTranslateOptions={{ name }}
        onConfirm={() => resetPassword()}
        onClose={() => setConfirmOpen(false)}
      />
      <TemporaryPasswordDialog
        open={temporaryPassword != null}
        name={name}
        password={temporaryPassword ?? ""}
        onClose={() => setTemporaryPassword(null)}
      />
    </div>
  );
};

/**
 * Users list. Everybody may read it (the "Behandelaar" dropdowns need it);
 * only administrators may create or edit a user. Users are never deleted,
 * they are disabled.
 */
export function SalesList() {
  const translate = useTranslate();
  const { canAccess: canManageUsers } = useCanAccess({
    resource: "sales",
    action: "create",
  });

  return (
    <List
      actions={<SalesListActions />}
      sort={{ field: "last_name", order: "ASC" }}
      perPage={25}
    >
      <DataTable<Sale>
        rowClick={canManageUsers ? "edit" : false}
        bulkActionButtons={false}
        className="bg-card"
      >
        <DataTable.Col<Sale>
          source="last_name"
          label="resources.sales.fields.name"
          render={(record) => `${record.first_name} ${record.last_name}`}
        />
        <DataTable.Col<Sale> source="email" />
        <DataTable.Col<Sale> source="administrator">
          <BooleanField source="administrator" FalseIcon={null} />
        </DataTable.Col>
        <DataTable.Col<Sale> source="disabled">
          <BooleanField source="disabled" FalseIcon={null} looseValue />
        </DataTable.Col>
        <DataTable.Col<Sale> label={false} className="w-12">
          <SaleRowActions />
        </DataTable.Col>
      </DataTable>
      <p className="text-xs text-muted-foreground mt-3">
        {translate("resources.sales.hint.no_delete")}
      </p>
    </List>
  );
}

/**
 * Mobile users list: title and search in the fixed header, the "new user"
 * button below it and one tappable row per user. Tapping a row opens the edit
 * form; the row menu also hands out a new temporary password.
 */
export const SalesListMobile = () => {
  const translate = useTranslate();

  return (
    <ListBase<Sale>
      perPage={100}
      sort={{ field: "last_name", order: "ASC" }}
      queryOptions={{
        onError: () => {
          /* the content below renders the error itself */
        },
      }}
    >
      <MobileListHeader
        title={translate("resources.sales.name", { smart_count: 2 })}
        searchPlaceholder={translate("resources.sales.filters.search")}
      />
      <MobileContent search>
        <CanAccess resource="sales" action="create">
          <Button asChild className="mb-4 h-11 w-full text-base">
            <Link to="/sales/create">
              <Plus className="size-5" />
              {translate("resources.sales.action.new")}
            </Link>
          </Button>
        </CanAccess>
        <SalesListContentMobile />
        <p className="text-xs text-muted-foreground mt-4">
          {translate("resources.sales.hint.no_delete")}
        </p>
      </MobileContent>
    </ListBase>
  );
};

const SalesListContentMobile = () => {
  const translate = useTranslate();
  const { data, error, isPending, refetch } = useListContext<Sale>();
  const redirect = useRedirect();
  const { canAccess: canManageUsers } = useCanAccess({
    resource: "sales",
    action: "create",
  });

  if (isPending) {
    return (
      <div className="divide-y rounded-lg border bg-card">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="px-4 py-3">
            <Skeleton className="mb-2 h-5 w-32" />
            <Skeleton className="h-4 w-48" />
          </div>
        ))}
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-4 text-center">
        <p className="mb-4 text-muted-foreground">
          {translate("resources.sales.list.error_loading")}
        </p>
        <Button onClick={() => refetch()}>
          <RotateCcw />
          {translate("crm.common.retry")}
        </Button>
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div
        className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground"
        role="status"
      >
        {translate("resources.sales.list.no_results")}
      </div>
    );
  }

  return (
    <ItemGroup className="divide-y overflow-hidden rounded-lg border bg-card">
      {data.map((record) => (
        <RecordContextProvider key={record.id} value={record}>
          <Item
            size="sm"
            className={
              canManageUsers ? "cursor-pointer rounded-none" : "rounded-none"
            }
            onClick={
              canManageUsers
                ? () => redirect("edit", "sales", record.id)
                : undefined
            }
          >
            <ItemContent className="min-w-0">
              <ItemTitle className="text-base">
                {record.first_name} {record.last_name}
              </ItemTitle>
              <ItemDescription className="break-all">
                {record.email}
              </ItemDescription>
              {record.administrator || record.disabled ? (
                <div className="mt-1 flex flex-wrap gap-1">
                  {record.administrator ? (
                    <Badge variant="secondary">
                      {translate("resources.sales.fields.administrator")}
                    </Badge>
                  ) : null}
                  {record.disabled ? (
                    <Badge variant="outline" className="text-muted-foreground">
                      {translate("resources.sales.fields.disabled")}
                    </Badge>
                  ) : null}
                </div>
              ) : null}
            </ItemContent>
            <ItemActions>
              <SaleRowActions />
            </ItemActions>
          </Item>
        </RecordContextProvider>
      ))}
    </ItemGroup>
  );
};
